/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Button, Icon, Label, List, Loader } from 'semantic-ui-react';

import { bem } from '../../../core/design/bem';
import platformService from '../../../core/services/platform.service';
import useUrlState from '../../../hooks/useUrlState';
import { useOrderBy } from '../../../utils/hooks';
import BlockContainer from '../../BlockContainer';
import FullscreenFormModal from '../../FormModal/FullscreenFormModal';
import { ContainerProps } from '../types';
import './RegistrationListBlock.scss';

/** Clé de query string utilisée pour mémoriser la personne en cours d'inscription */
const URL_STATE_KEY = 'registration';

const css = bem('RegistrationListBlock');
const translationPrefix = 'blocks.registration-list';

type Person = {
  _id: string;
  privateKey?: string;
  firstName?: string;
  lastName?: string;
  status?: string;
  [x: string]: any;
};

type RegistrationListBlockProps = {
  /** Collection des personnes à afficher (défaut `participants`) */
  collection?: string;
  /** Slug du formulaire d'inscription à ouvrir */
  formSlug: string;
  container?: ContainerProps;
  orderBy?: { field: string; order: 'asc' | 'desc' }[];
};

function getPersonName(person: Person, fallback: string): string {
  const name = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
  return name || fallback;
}

type StatusInfo = { label: string; color?: 'green' | 'red'; basic: boolean };

function getStatusInfo(
  status: string | undefined,
  t: (key: string | string[]) => string,
): StatusInfo {
  if (!status || status === 'invited') {
    return { label: t(`${translationPrefix}.status.not-registered`), basic: true };
  }
  if (status === 'registered') {
    return { label: t(`${translationPrefix}.status.registered`), color: 'green', basic: false };
  }
  if (status === 'refused') {
    return { label: t(`${translationPrefix}.status.refused`), color: 'red', basic: false };
  }
  // Statut inconnu : on affiche la valeur brute
  return { label: t([`${translationPrefix}.status.${status}`, status]), basic: true };
}

export const RegistrationListBlock = ({
  collection = 'participants',
  formSlug,
  container,
  orderBy = [{ field: 'lastName', order: 'asc' }],
}: RegistrationListBlockProps): JSX.Element => {
  const { t } = useTranslation();
  const [urlState, setUrlState] = useUrlState({ [URL_STATE_KEY]: undefined });
  const selectedId = urlState[URL_STATE_KEY];

  const { data = [], isLoading, refetch } = useQuery(
    ['registration-list', collection],
    () => platformService.fetchMyChildren(collection) as Promise<Person[]>,
    { refetchOnWindowFocus: true },
  );

  const people = useOrderBy(data, orderBy) as Person[];
  const selected = useMemo(() => people.find((p) => p._id === selectedId), [people, selectedId]);

  const openRegistration = (person: Person) => setUrlState({ [URL_STATE_KEY]: person._id });
  const handleClose = () => setUrlState({ [URL_STATE_KEY]: undefined });
  const handleCompleted = () => {
    setUrlState({ [URL_STATE_KEY]: undefined });
    refetch();
  };

  return (
    <BlockContainer {...(container as any)} className={css().toString()}>
      {isLoading && <Loader active inline="centered" />}
      {!isLoading && people.length === 0 && (
        <div className={css('empty').toString()}>{t(`${translationPrefix}.empty`)}</div>
      )}
      <List divided relaxed verticalAlign="middle" className={css('list').toString()}>
        {people.map((person) => {
          const status = getStatusInfo(person.status, t);
          return (
            <List.Item key={person._id} className={css('item').toString()}>
              <List.Content className={css('info').toString()}>
                <span className={css('name').toString()}>
                  {getPersonName(person, t(`${translationPrefix}.anonymous`))}
                </span>
                <Label
                  size="small"
                  color={status.color}
                  basic={status.basic}
                  className={css('status').toString()}
                >
                  {status.label}
                </Label>
              </List.Content>
              <List.Content className={css('action').toString()}>
                <Button primary size="small" onClick={() => openRegistration(person)}>
                  <Icon name="edit outline" />
                  {t(`${translationPrefix}.register`)}
                </Button>
              </List.Content>
            </List.Item>
          );
        })}
      </List>

      {selected && (
        <FullscreenFormModal
          formSlug={formSlug}
          title={getPersonName(selected, t(`${translationPrefix}.register`))}
          privateKey={selected.privateKey}
          onClose={handleClose}
          onCompleted={handleCompleted}
        />
      )}
    </BlockContainer>
  );
};

export default RegistrationListBlock;
